import { useState } from "react";
import { addDoc, collection, doc, setDoc } from "firebase/firestore";
import db from "../../firebase/firebase";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Collapse
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";

export default function AddEditQuiz() {
    const location = useLocation();
    const navigate = useNavigate();
    const quiz = location.state?.quiz || null;

    const [title, setTitle] = useState(quiz?.title || "");
    const [description, setDescription] = useState(quiz?.description || "");
    const [questions, setQuestions] = useState(quiz?.questions || []);

    // 🔥 controls which question is expanded
    const [openIndex, setOpenIndex] = useState(
        quiz?.questions?.length ? quiz.questions.length - 1 : null
    )

    const addQuestion = () => {
        const newIndex = questions.length;

        setQuestions([
            ...questions,
            {
                number: newIndex + 1,
                question: "",
                options: { A: "", B: "", C: "", D: "" },
                answer: "",
                explanation: ""
            }
        ]);

        setOpenIndex(newIndex); // expand last question
    }

    const saveQuiz = async () => {
        const payload = { title, description, questions };

        if (quiz?.id) {
            await setDoc(doc(db, "quizzes", quiz.id), payload);
        } else {
            await addDoc(collection(db, "quizzes"), payload);
        }

        navigate(-1);
    };

    const updateQuestion = (index, field, value) => {
        const copy = [...questions];
        copy[index][field] = value;
        setQuestions(copy);
    };

    const updateOption = (qIndex, key, value) => {
        const copy = [...questions];
        copy[qIndex].options[key] = value;
        setQuestions(copy);
    };

    const deleteQuestion = (index) => {
        const updated = questions.filter((_, i) => i !== index);
        // re-number questions
        const renumbered = updated.map((q, i) => ({
            ...q,
            number: i + 1
        }));
        setQuestions(renumbered);
        // handle collapse state
        if (openIndex === index) {
            setOpenIndex(index > 0 ? index - 1 : null);
        } else if (openIndex > index) {
            setOpenIndex(openIndex - 1);
        }
    };

    return (
        <Container fluid className="py-4 admin-quiz-editor">
            <Button color="link" onClick={() => navigate(-1)}>
                &larr; Back
            </Button>

            <h3 className="mb-4">
                {quiz ? "Edit Quiz" : "Add Quiz"}
            </h3>

            {/* Quiz Info */}
            <Card className="mb-4 admin-quiz-meta">
                <CardBody>
                    <Form>
                        <FormGroup>
                            <Label>Quiz Title</Label>
                            <Input
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Description</Label>
                            <Input
                                type="textarea"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </FormGroup>
                    </Form>
                </CardBody>
            </Card>

            {/* Questions */}
            {questions.map((q, i) => (
                <Card className="mb-3 admin-question-card" key={i}>
                    <CardBody>
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center">
                            <div
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                    setOpenIndex(openIndex === i ? null : i)
                                }
                            >
                                <h6 className="mb-0">Question {i + 1}</h6><div className="admin-question-preview">{q.question || "New question"}</div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <span
                                    style={{ cursor: "pointer", color:'blue', marginRight:10 }}
                                    onClick={() =>
                                        setOpenIndex(openIndex === i ? null : i)
                                    }>
                                    {openIndex === i ? "Collapse" : "Expand"}
                                </span>

                                <Button
                                    size="sm"
                                    color="danger"
                                    outline
                                    onClick={() => deleteQuestion(i)}>
                                    Delete
                                </Button>
                            </div>
                        </div>


                        {/* Collapsible content */}
                        <Collapse isOpen={openIndex === i}>
                            <div className="mt-3">
                                <FormGroup>
                                    <Label>Question</Label>
                                    <Input
                                        type="textarea"
                                        placeholder="Enter question"
                                        value={q.question}
                                        onChange={e =>
                                            updateQuestion(i, "question", e.target.value)
                                        }
                                    />
                                </FormGroup>

                                {Object.keys(q.options).map(key => (
                                    <FormGroup key={key} className="admin-option-row">
                                        <Label className="admin-option-letter">{key}</Label>
                                        <Input
                                            aria-label={`Option ${key}`}
                                            placeholder={`Enter option ${key}`}
                                            value={q.options[key]}
                                            onChange={e =>
                                                updateOption(i, key, e.target.value)
                                            }
                                        />
                                    </FormGroup>
                                ))}

                                <FormGroup>
                                    <Label>Correct Answer</Label>
                                    <Input
                                        type="select"
                                        value={q.answer}
                                        onChange={e =>
                                            updateQuestion(i, "answer", e.target.value)
                                        }>
                                        <option value="">Select correct answer</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label>Explanation</Label>
                                    <Input
                                        type="textarea"
                                        placeholder="Explain why the correct answer is correct"
                                        value={q.explanation}
                                        onChange={e =>
                                            updateQuestion(i, "explanation", e.target.value)
                                        }
                                    />
                                </FormGroup>
                            </div>
                        </Collapse>
                    </CardBody>
                </Card>
            ))}

            {/* Actions */}
            <Row className="mt-3">
                <Col className="d-flex gap-2">
                    <Button color="secondary" onClick={addQuestion}>
                        Add Question
                    </Button>
                    <Button color="primary" onClick={saveQuiz}>
                        Save Quiz
                    </Button>
                </Col>
            </Row>
        </Container>
    );
}
